<template>
  <ModalDialog
    ref="modal"
    data-testid="agency-send-reminder-dialog"
    persistent
    max-height="95vh"
    max-width="648px"
    :title="t('Reviews.agency.sendReminderDialog.title')"
    @closed="$emit('closed')"
  >
    <div class="mb-6 text-body-s">{{ t('Reviews.agency.sendReminderDialog.text') }}</div>
    <div class="mb-1.5 flex items-end justify-between gap-2 text-label-m">
      <span>{{ t('Reviews.agency.sendReminderDialog.messageLabel') }}</span>
      <button v-if="focused" class="btn-outline-neutral-xs" @mousedown.prevent @click="insertLink">
        {{ t('Reviews.agency.sendReminderDialog.insertReviewLink') }}
      </button>
      <TooltipWrapper v-else :z-index="60">
        <template #trigger>
          <button class="btn-outline-neutral-xs" disabled @click="insertLink">
            {{ t('Reviews.agency.sendReminderDialog.insertReviewLink') }}
          </button>
        </template>
        <template #content>
          <div class="max-w-xs" v-html="t('Reviews.agency.sendReminderDialog.insertReviewLinkHint')" />
        </template>
      </TooltipWrapper>
    </div>
    <RichInput
      id="agency-reminder-message-input"
      ref="richInput"
      :key="inputKey"
      v-model="v$.message.$model"
      :error="getValidationErrors(v$.message)"
      hide-controls
      plain-text-only
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    />
    <div v-if="!reviewerFirstName" class="mt-1.5 text-body-s text-subtle">
      {{ t('Reviews.agency.sendReminderDialog.salutationFallback') }}
    </div>
    <div class="mt-4 flex items-center justify-between">
      <CheckBox v-model="v$.copyToSender.$model">{{ t('Reviews.agency.sendReminderDialog.copyToSender') }}</CheckBox>
      <button class="btn-primary-color-s" data-testid="agency-reminder-submit" :disabled="loading || v$.$invalid" @click="onSubmit">
        {{ t('Reviews.agency.sendReminderDialog.send') }}
      </button>
    </div>
  </ModalDialog>
</template>
<script setup lang="ts">
import { CheckBox, ModalDialog, TooltipWrapper, nl2br } from '@ramp106/omrjs-core-ui'
import { RichInput } from '@ramp106/omrjs-core-ui/deprecated'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { storeToRefs } from 'pinia'
import TurnDown from 'turndown'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCreateUniversalReviewReminderMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, maxLength, minLength, containsValidator, withI18nMessage } from '@/services/validators'
import { useMeStore } from '@/stores'

type FormData = {
  message: string
  copyToSender: boolean
}

const MSG_MIN_LENGTH = 100
const MSG_MAX_LENGTH = 10000
const REVIEW_LINK_BE_PLACEHOLDER = '%{review_link}'
const REVIEW_LINK_PLACEHOLDER = `(${REVIEW_LINK_BE_PLACEHOLDER})`

const props = defineProps<{
  universalReviewId: string
  entityId: string
  reviewerFirstName?: string
}>()

defineEmits(['closed'])

const meStore = useMeStore()
const { me } = storeToRefs(meStore)
const modal = ref<InstanceType<typeof ModalDialog> | null>(null)
const richInput = ref<InstanceType<typeof RichInput> | null>(null)
const inputKey = ref(1)
const focused = ref(false)
const { t } = useI18n()

const { loading, mutate: createReminder } = useCreateUniversalReviewReminderMutation({ clientId: 'reviews' })

const reviewLink = computed(() => `<a href="${REVIEW_LINK_BE_PLACEHOLDER}">${t('Reviews.agency.sendReminderDialog.reviewLinkText')}</a> `)

const messageBody = computed(() =>
  t('Reviews.agency.sendReminderDialog.messageBody', {
    authorName: props.reviewerFirstName || t('Reviews.agency.sendReminderDialog.salutationFallbackMessageBody'),
    vendorName: me.value?.partnerCompany?.displayName || '',
    reviewLink: reviewLink.value,
  }).toString(),
)

const formData = reactive<FormData>({
  message: messageBody.value,
  copyToSender: false,
})

const containsLink = withI18nMessage(containsValidator, {
  withArguments: true,
  messagePath: () => 'Reviews.agency.sendReminderDialog.missingReviewLink',
})

const rules = computed<ValidationArgs<FormData>>(() => ({
  message: {
    required,
    minLength: minLength(MSG_MIN_LENGTH),
    maxLength: maxLength(MSG_MAX_LENGTH),
    contains: containsLink(REVIEW_LINK_BE_PLACEHOLDER),
  },
  copyToSender: {},
}))

const v$ = useVuelidate(rules, formData)

function insertLink() {
  richInput.value?.insertHTML(REVIEW_LINK_PLACEHOLDER)
  v$.value.message.$model = v$.value.message.$model.replace(REVIEW_LINK_PLACEHOLDER, reviewLink.value)
  inputKey.value = inputKey.value + 1
}

function open() {
  modal.value?.open()
}

function close() {
  modal.value?.close()
}

function onFocusIn() {
  focused.value = true
}

function onFocusOut() {
  setTimeout(() => (focused.value = false), 100)
}

async function onSubmit() {
  const isFormValid = await v$.value.$validate()
  if (!isFormValid) {
    showNotification(t('Reviews.agency.sendReminderDialog.invalidForm'), 'error')
    return
  }

  track('review_remind', {
    surface: 'editor',
    reminder_type: 'invite_to_complete',
    entity_type: 'service',
    entity_id: props.entityId,
    scope: 'single',
    survey_response_id: props.universalReviewId,
  })

  const turnDown = new TurnDown({
    emDelimiter: '_',
    linkStyle: 'inlined',
    headingStyle: 'atx',
  })

  try {
    const result = await createReminder({
      input: {
        universalReviewId: props.universalReviewId,
        message: turnDown.turndown(nl2br(formData.message)),
        sendCopyToVendor: formData.copyToSender,
      },
    })
    if (!result?.data?.manage.createUniversalReviewReminder) {
      showNotification(t('Reviews.agency.sendReminderDialog.serverError'), 'error', 10000)
      return
    }
    showNotification(t('Reviews.agency.sendReminderDialog.success', { authorName: props.reviewerFirstName }), 'success', 10000)
    close()
  } catch (error) {
    const graphQlMessage = (error as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message
    showNotification(graphQlMessage || t('Reviews.agency.sendReminderDialog.serverError'), 'error', 10000)
  }
}

defineExpose({ open, close })
</script>
